import React, { useContext } from "react";
import { GetServerSideProps } from "next";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { UserContext } from "context/user";
import { LogContext } from "context/log";

import { Cart as PepCart } from "components/RockstarCart/Cart";
import { getCheckoutUrl } from "helpers/cart";
import { logCartCheckoutButtonClickEvent } from "@pepdirect/helpers/analyticsLogger";
import { getSubtotal, getErrors } from "@pepdirect/helpers/cart";
import { convertFromDollarsToCents } from "@pepdirect/helpers";
import { cart as cartConfig, endpoints, shipping } from "config";
import RockstarHeader from "components/RockstarHeader";
import RockstarFooter from "components/RockstarFooter";
import { WpYoastHead } from "types/wp-yoast-head";
import HeadTagsRenderer from "components/HeadTagsRenderer";
import { DatoProductFragment, getSdk } from "services/datocms/generated";
import { createDatoClient } from "services/datocms";
import TopCtaBanner from "components/TopCtaBanner";

interface CartPageProps {
  products: DatoProductFragment[];
}

export const getServerSideProps: GetServerSideProps<CartPageProps> = async (
  appContext
) => {
  try {
    const { allProducts } = await getSdk(
      createDatoClient(appContext.preview)
    ).products();
    return {
      props: {
        products: allProducts,
      },
    };
  } catch (error) {
    return {
      props: {
        products: [],
      },
    };
  }
};

export default function Cart({ products }: CartPageProps): JSX.Element {
  const { useLogPageView } = useContext(LogContext);
  useLogPageView(`cart`);

  const { tenant } = useContext(AppContext);
  const { cart, getSize, updateCartItem } = useContext(CartContext);
  const { currentUserId } = useContext(UserContext);
  const { useLogCartViewed } = useContext(LogContext);

  useLogCartViewed(cart);

  const checkoutUrl = getCheckoutUrl();
  const size = getSize();

  const minPurchaseAmt = tenant?.shoppingBagConfig?.minimumPurchaseAmount || 0;
  const { maxCartQty: maxCartQtyDefault } = cartConfig;
  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;

  const errors = getErrors(cart, currentUserId);

  const subtotal = getSubtotal(cart);
  const freeShipping = convertFromDollarsToCents(subtotal) >= minPurchaseAmt;
  const total = freeShipping
    ? subtotal
    : subtotal + parseInt(shipping.us.below);
  const infoText =
    !!minPurchaseAmt && !freeShipping
      ? `Free shipping on all orders ($${
          minPurchaseAmt / 100
        } minimum purchase)`
      : "";

  const goToCheckout = () => {
    if (!cart) return;
    logCartCheckoutButtonClickEvent(currentUserId, cart);
    window.open(checkoutUrl, "_self");
  };

  const seoTags: WpYoastHead = {
    title: "Cart - Rockstar Energy",
    description:
      "Keep that same energy with our offering of Rockstar Energy Products, made with delicious fruit flavored energy to keep you on your grind.",
    canonical: `${endpoints.web}/cart/`,
    og_locale: "en_US",
    og_type: "article",
    og_title: "Cart - Rockstar Energy",
    og_description:
      "Keep that same energy with our offering of Rockstar Energy Products, made with delicious fruit flavored energy to keep you on your grind.",
    og_url: `${endpoints.web}`,
    og_site_name: "Rockstar Energy",
    twitter_card: "summary_large_image",
  };

  return (
    <>
      <HeadTagsRenderer yoastJson={seoTags} />

      <div id="page" className="site">
        <a className="skip-link screen-reader-text" href="#content">
          Skip to content
        </a>

        <div className="site-header-block">
          <TopCtaBanner />
          <RockstarHeader showMusicPlayer={true} />
        </div>

        <div id="content" className="site-content">
          <div id="primary" className="content-area">
            <main id="main" className="site-main" role="main">
              {size === 0 ? (
                <h1
                  className="container has-text-align-center"
                  style={{ margin: "250px 0" }}
                >
                  YOUR CART IS EMPTY!
                </h1>
              ) : (
                <PepCart
                  cmsProducts={products}
                  size={size}
                  cart={cart}
                  blockedItems={[]}
                  subtotal={subtotal}
                  total={total}
                  currentUserId={currentUserId}
                  maxCartQty={maxCartQty}
                  infoText={infoText}
                  productsImgCdnEndpoint={endpoints.productsImgCdn}
                  errors={errors}
                  freeShipping={freeShipping}
                  isButtonDisabled={!!errors?.length || !freeShipping}
                  updateCartItem={updateCartItem}
                  goToCheckout={goToCheckout}
                />
              )}
            </main>
          </div>
        </div>

        <RockstarFooter />
      </div>
    </>
  );
}
